/**
 * Agent runs: one switch on the Repositories tab of Connections that says
 * whether Wright loads this repository's own committed skills, commands,
 * rules and plugins when it builds (YAG-784). On by default since YAG-1006;
 * an owner switches it off, and off means a run reads only AGENTS.md and
 * CLAUDE.md from the repository.
 */
import { Switch } from "../ui/switch.js";
import {
  AGENT_TRUST_COPY,
  AGENT_TRUST_READ_ONLY,
  AGENT_TRUST_TITLE,
} from "../lib/agentSourcesCopy.js";
import type { useRepoAgentTrust } from "./useRepoAgentTrust.js";

type AgentTrustState = ReturnType<typeof useRepoAgentTrust>;

/** The Skills row's summary: what a build loads from this repository. */
export function agentTrustSummary({ trust, error }: AgentTrustState): string {
  if (!trust) return error ? "Could not be read just now" : "Reading";
  return trust.trusted
    ? "Its committed skills, commands, rules and plugins"
    : "Only AGENTS.md and CLAUDE.md";
}

export function RepositoryAgentTrust({ state }: { state: AgentTrustState }) {
  const { trust, busy, error, setTrusted } = state;
  return (
    <div data-testid="repository-agent-trust">
      {error && (
        <p role="alert" className="qa-notice">
          {error}
        </p>
      )}
      <div className="wrk-srow">
        <span className="wrk-srow-text">
          <span className="wrk-srow-title">{AGENT_TRUST_TITLE}</span>
          <span className="wrk-srow-help">{AGENT_TRUST_COPY}</span>
          {trust && !trust.canEdit && (
            <span className="wrk-srow-help">{AGENT_TRUST_READ_ONLY}</span>
          )}
        </span>
        <span className="wrk-srow-controls">
          {/* No switch until the read settles, so an off repository never
                shows on; a failed read shows the default, disabled. */}
          {(trust || error) && (
            <Switch
              aria-label={AGENT_TRUST_TITLE}
              checked={trust?.trusted ?? true}
              disabled={!trust || !trust.canEdit || busy}
              onCheckedChange={(next) => void setTrusted(next)}
            />
          )}
        </span>
      </div>
    </div>
  );
}
